import React, { useState, useEffect, useCallback } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/hooks/use-toast';
import {
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  AlertCircle,
  Database,
  Users,
  Activity,
  Wrench,
  ChevronRight,
  Zap,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function DataSyncStatus({ compact = false, showFixButton = true }) {
  const { toast } = useToast();
  const [syncStatus, setSyncStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fixWizardOpen, setFixWizardOpen] = useState(false);
  const [fixWizardData, setFixWizardData] = useState(null);
  const [caseloadEntryOpen, setCaseloadEntryOpen] = useState(false);
  const [nursesForEntry, setNursesForEntry] = useState([]);
  const [caseloadEdits, setCaseloadEdits] = useState({});
  const [saving, setSaving] = useState(false);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('qapi-token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }, []);

  const fetchSyncStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/careflow/sync-status`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setSyncStatus(data);
      }
    } catch (error) {
      console.error('Error fetching sync status:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  const fetchFixWizard = async () => {
    try {
      const response = await fetch(`${API_URL}/api/careflow/data-fix-wizard`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setFixWizardData(data);
        setFixWizardOpen(true);
      }
    } catch (error) {
      console.error('Error fetching fix wizard:', error);
    }
  };

  const fetchNursesForEntry = async () => {
    try {
      const response = await fetch(`${API_URL}/api/productivity/nurses-for-caseload-entry`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setNursesForEntry(data.nurses || []);
        // Initialize edits with current values
        const edits = {};
        data.nurses?.forEach(n => {
          edits[n.clinician_name] = n.current_caseload || '';
        });
        setCaseloadEdits(edits);
        setCaseloadEntryOpen(true);
      }
    } catch (error) {
      console.error('Error fetching nurses:', error);
    }
  };

  const saveCaseloads = async () => {
    setSaving(true);
    try {
      // Build updates array
      const updates = Object.entries(caseloadEdits)
        .filter(([name, value]) => value !== '' && value !== null)
        .map(([clinician_name, total_patients]) => ({
          clinician_name,
          total_patients: parseInt(total_patients),
        }));

      if (updates.length === 0) {
        toast({
          title: 'No Changes',
          description: 'No caseload values were entered',
        });
        return;
      }

      const response = await fetch(`${API_URL}/api/productivity/nurse-caseload/bulk`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(updates),
      });

      if (response.ok) {
        const result = await response.json();
        toast({
          title: 'Caseloads Updated',
          description: result.message,
        });
        setCaseloadEntryOpen(false);
        fetchSyncStatus();
      } else {
        toast({
          title: 'Update Failed',
          description: 'Unable to save caseloads',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Network error saving caseloads',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const autoDistribute = async () => {
    try {
      const response = await fetch(`${API_URL}/api/careflow/auto-distribute-caseloads`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const result = await response.json();
        toast({
          title: 'Caseloads Distributed',
          description: result.message,
        });
        setFixWizardOpen(false);
        fetchSyncStatus();
      } else {
        const error = await response.json();
        toast({
          title: 'Distribution Failed',
          description: error.detail,
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Network error during distribution',
        variant: 'destructive',
      });
    }
  };

  useEffect(() => {
    fetchSyncStatus();
    // Refresh every 60 seconds
    const interval = setInterval(fetchSyncStatus, 60000);
    return () => clearInterval(interval);
  }, [fetchSyncStatus]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'bg-green-500';
      case 'warning': return 'bg-yellow-500';
      case 'critical': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'warning': return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      case 'critical': return <AlertCircle className="w-4 h-4 text-red-500" />;
      default: return <Database className="w-4 h-4 text-gray-500" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <RefreshCw className="w-4 h-4 animate-spin" />
        <span className="text-sm">Checking sync...</span>
      </div>
    );
  }

  if (compact) {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="sm" className="gap-2" data-testid="sync-status-btn">
            <div className={`w-2 h-2 rounded-full ${getStatusColor(syncStatus?.status)}`} />
            <span className="text-xs">Data Sync</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-medium">Data Sync Status</h4>
              {getStatusIcon(syncStatus?.status)}
            </div>
            
            <div className="space-y-2">
              {Object.entries(syncStatus?.modules || {}).map(([name, module]) => (
                <div key={name} className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{name.replace('_', ' ')}</span>
                  <Badge variant={module.status === 'active' ? 'default' : 'secondary'}>
                    {module.status}
                  </Badge>
                </div>
              ))}
            </div>

            {syncStatus?.issues?.length > 0 && (
              <Alert variant="destructive" className="py-2">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  {syncStatus.issues.length} issue(s) found
                </AlertDescription>
              </Alert>
            )}

            {showFixButton && syncStatus?.issues?.length > 0 && (
              <Button size="sm" className="w-full gap-2" onClick={fetchFixWizard}>
                <Wrench className="w-4 h-4" />
                Fix Issues
              </Button>
            )}
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <>
      <div className="flex items-center gap-3 p-3 bg-white/50 dark:bg-slate-800/50 rounded-lg border" data-testid="sync-status-panel">
        <div className={`w-3 h-3 rounded-full ${getStatusColor(syncStatus?.status)}`} />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm">Data Sync</span>
            <Badge variant={syncStatus?.status === 'healthy' ? 'default' : 'destructive'}>
              {syncStatus?.status?.toUpperCase()}
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground">
            {syncStatus?.issues?.length > 0 
              ? `${syncStatus.issues.length} issue(s) need attention`
              : 'All modules synced'
            }
          </p>
        </div>
        {showFixButton && syncStatus?.issues?.length > 0 && (
          <Button size="sm" variant="outline" className="gap-2" onClick={fetchFixWizard}>
            <Wrench className="w-4 h-4" />
            Fix
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={fetchSyncStatus}>
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Fix Wizard Dialog */}
      <Dialog open={fixWizardOpen} onOpenChange={setFixWizardOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="w-5 h-5 text-blue-500" />
              Data Consistency Fix Wizard
            </DialogTitle>
            <DialogDescription>
              Follow these steps to resolve data issues and ensure accurate reporting.
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="max-h-[400px]">
            <div className="space-y-4 pr-4">
              {fixWizardData?.fix_steps?.map((step, idx) => (
                <div key={idx} className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <Badge variant={step.priority === 1 ? 'destructive' : step.priority === 2 ? 'warning' : 'secondary'}>
                        Priority {step.priority}
                      </Badge>
                      <span className="font-medium">{step.issue}</span>
                    </div>
                  </div>
                  
                  <p className="text-sm text-muted-foreground">{step.impact}</p>
                  
                  <div className="bg-slate-50 dark:bg-slate-800 rounded p-3">
                    <p className="text-sm font-medium mb-2">How to fix:</p>
                    <ul className="text-sm space-y-1">
                      {step.how_to_fix?.map((fix, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <ChevronRight className="w-4 h-4 mt-0.5 text-muted-foreground" />
                          {fix}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  {step.quick_fix_available && step.priority === 2 && (
                    <Button size="sm" className="gap-2" onClick={fetchNursesForEntry}>
                      <Users className="w-4 h-4" />
                      Edit Nurse Caseloads
                    </Button>
                  )}
                  
                  {step.quick_fix_available && step.priority === 3 && (
                    <Button size="sm" className="gap-2" onClick={autoDistribute}>
                      <Zap className="w-4 h-4" />
                      Auto-Distribute Census
                    </Button>
                  )}
                </div>
              ))}
              
              {(!fixWizardData?.fix_steps || fixWizardData.fix_steps.length === 0) && (
                <div className="text-center py-8">
                  <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-3" />
                  <p className="font-medium text-green-600">No issues found!</p>
                  <p className="text-sm text-muted-foreground">Your data is consistent across all modules.</p>
                </div>
              )}
            </div>
          </ScrollArea>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setFixWizardOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Caseload Entry Dialog */}
      <Dialog open={caseloadEntryOpen} onOpenChange={setCaseloadEntryOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-violet-500" />
              Edit Nurse Caseloads
            </DialogTitle>
            <DialogDescription>
              Enter the number of patients assigned to each nurse.
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="max-h-[400px]">
            <div className="space-y-3 pr-4">
              {nursesForEntry.map((nurse) => (
                <div key={nurse.clinician_name} className="flex items-center gap-4 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <div className="flex-1">
                    <p className="font-medium">{nurse.clinician_name}</p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Badge variant="outline" className="text-xs">{nurse.team || 'No Team'}</Badge>
                      <span>{nurse.total_visits} visits</span>
                      {nurse.needs_update && (
                        <Badge variant="destructive" className="text-xs">Needs Update</Badge>
                      )}
                    </div>
                  </div>
                  <div className="w-32">
                    <Label className="text-xs">Patients</Label>
                    <Input
                      type="number"
                      min="0"
                      max="50"
                      value={caseloadEdits[nurse.clinician_name] || ''}
                      onChange={(e) => setCaseloadEdits({
                        ...caseloadEdits,
                        [nurse.clinician_name]: e.target.value
                      })}
                      placeholder={nurse.current_caseload || 'Enter'}
                      className="h-8"
                    />
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCaseloadEntryOpen(false)}>Cancel</Button>
            <Button onClick={saveCaseloads} disabled={saving} className="gap-2">
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
              Save Caseloads
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
